import { faBox, faBoxesStacked, faEdit, faFilePdf, faPencilAlt, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { X, Eye, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { decryptData } from "../../../utils/storageCrypto";

const RenderDeliveryGroupMapping = ({ deliveryGroup, handleGetDg, setShowPopup, setPopupType, setPopupMessage }) => {
  const navigate = useNavigate();
  const [showSlotPopup, setShowSlotPopup] = useState(false);
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("");
  
  const [shiftPopupVisible, setShiftPopupVisible] = useState(false);
  const [shiftToValue, setShiftToValue] = useState("");
  const [billToValue, setBillToValue] = useState("");
  
  const API_BASE = process.env.REACT_APP_FORE_BRICKBUCKET + 'customer/salesorder';
  const imageurl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;
  
  // Array-based states for handling multi-PO bills & invoices
  const [billPopup, setBillPopup] = useState(false);
  const [billsData, setBillsData] = useState([]);
  const [billStates, setBillStates] = useState({});
  const [invoicesPopup, setInvoicesPopup] = useState(false);
  const [invoicesData, setInvoicesData] = useState([]);

  const [showPreview, setShowPreview] = useState(false);
  const [subOrderData, setSubOrderData] = useState(null);
  
  const [expandedPOs, setExpandedPOs] = useState([]);
  const [isInitiateBillingLoading, setIsInitiateBillingLoading] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  useEffect(() => {
    if (deliveryGroup?.sub_orders) {
      setExpandedPOs(deliveryGroup.sub_orders.map(po => po._id));
    }
  }, [deliveryGroup]);

  useEffect(() => {
    if (billsData.length > 0) {
      const initialState = {};
      billsData.forEach(b => {
        initialState[b._id] = {
          billNumber: b.bill_number || "",
          editBill: false,
          rejectReason: b.reject_reason || "",
          showRejectReason: !!b.reject_reason
        };
      });
      setBillStates(initialState);
    }
  }, [billsData]);

  const updateBillState = (billId, key, value) => {
    setBillStates(prev => ({ 
      ...prev, 
      [billId]: { 
        ...(prev[billId] || {}), 
        [key]: value 
      } 
    }));
  };

  const isBillingInitiated = deliveryGroup?.billing_status === "Ready To Bill" || deliveryGroup?.billing_status === "Initiated";
  const isBilled = deliveryGroup?.billing_status === "Billed";
  const isBillAccepted = isBilled && deliveryGroup?.bill_accepted_status?.toLowerCase() === "accepted";
  const isInvoiceGenerated = deliveryGroup?.invoice_status === "Invoice Generated";
  const isReadyForDelivery = deliveryGroup?.sub_orders?.[0]?.status === "Ready For Delivery";

  const handleGenerateBill = async () => {
    if (!deliveryGroup) return;
    try {
      setIsInitiateBillingLoading(true);
      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);

      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}bill/award-order-and-generate-bills`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify({ delivery_group_ids: [deliveryGroup._id] }),
      });
      const data = await response.json();

      if (data.success) {
        setPopupType('200or201');
        setPopupMessage("Bill generated successfully!");
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 1000);
        handleGetDg();
      } else {
        setPopupType('400');
        setPopupMessage(data.message || "Failed to generate bill");
        setShowPopup(true);
      }
    } catch (error) {
      setPopupType('500');
      setPopupMessage("Something went wrong!");
      setShowPopup(true);
    } finally {
      setIsInitiateBillingLoading(false);
    }
  };

  const handleGenerateInvoices = async () => {
    try {
      setIsGeneratingInvoice(true);
      if (!deliveryGroup?.sub_orders?.length) return;

      for (const subOrder of deliveryGroup.sub_orders) {
        const poId = subOrder?.purchaseorder_id;
        if (!poId) continue;
        const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);

        await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}invoice/create-invoices-for-order-using-poids`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-token": token },
          body: JSON.stringify({ po_ids: [poId] }),
        });
      }

      setPopupType("200or201");
      setPopupMessage("Invoices generated successfully");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      handleGetDg();
    } catch (error) {
      setPopupType("400");
      setPopupMessage("Failed to generate invoices");
      setShowPopup(true);
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const saveSlotUpdate = async () => {
    try {
      if (!deliveryGroup._id) return;
      const payload = {
        delivery_grouping_id: deliveryGroup._id,
        delivery_date: slotDate || undefined,
        delivery_slot: slotTime || undefined
      };
      const token = decryptData(sessionStorage.getItem('token'));
      const response = await fetch(`${API_BASE}/update-delivery-grouping`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        body: JSON.stringify(payload)
      });
      const result = await response.json(); 

      if (result.success) {
        setShowSlotPopup(false);
        handleGetDg();
      } else {
        setShowSlotPopup(false);
      }
    } catch (err) { setShowSlotPopup(false); }
  };

  const handleAcceptBill = async (billId) => {
    try {
      const token = decryptData(sessionStorage.getItem('token'));
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}bill/accept-supplier-bill`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify({ bill_id: billId })
      });
      const result = await response.json();
      if (result.success) {
        setPopupType('200or201'); setPopupMessage("Bill accepted successfully!"); setShowPopup(true);
        setBillPopup(false);
        handleGetDg();
        setTimeout(() => setShowPopup(false), 2000);
      } else {
        setPopupType('400'); setPopupMessage(result.message || "Failed to accept bill"); setShowPopup(true);
      }
    } catch (err) {
      setPopupType('500'); setPopupMessage("Something went wrong!"); setShowPopup(true);
    }
  };

  const handleRejectBill = async (billId, reason) => {
    try {
      const token = decryptData(sessionStorage.getItem('token'));
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}bill/reject-supplier-bill`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify({ bill_id: billId, reject_reason: reason })
      });
      const result = await response.json();
      if (result.success) {
        setPopupType('200or201'); setPopupMessage("Bill rejected successfully!"); setShowPopup(true);
        setBillPopup(false);
        handleGetDg();
        setTimeout(() => setShowPopup(false), 2000);
      } else {
        setPopupType('400'); setPopupMessage(result.message || "Failed to reject bill"); setShowPopup(true);
      }
    } catch (err) {
      setPopupType('500'); setPopupMessage("Something went wrong!"); setShowPopup(true);
    }
  };

  const updateBillNumber = async (billId, billNum, rejReason) => {
    try {
      const token = decryptData(sessionStorage.getItem('token'));
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}bill/update-bill`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-token": token },
        body: JSON.stringify({ billId: billId, updateData: { bill_number: billNum, reject_reason: rejReason || undefined } })
      });
      const result = await response.json();
      if (result.success) {
        setPopupType('200or201'); setPopupMessage("Bill updated successfully!");
        setBillPopup(false); setShowPopup(true);
        handleGetDg();
        setTimeout(() => setShowPopup(false), 3000);
      } else {
        setPopupType('400'); setPopupMessage(result.message); setShowPopup(true);
      }
    } catch (err) {
      setPopupType('500'); setPopupMessage("Something went wrong!"); setShowPopup(true);
    }
  };

  const openSlotPopup = () => {
    setSlotDate(deliveryGroup?.delivery_date ? deliveryGroup.delivery_date.split("T")[0] : "");
    setSlotTime(deliveryGroup?.delivery_slot || "");
    setShowSlotPopup(true);
  };

  const openEditPopup = () => {
    setShiftToValue(deliveryGroup?.is_delivered_by_brickbucket ? "Brickbucket" : "Customer");
    setBillToValue(deliveryGroup?.bill_to || "Brickbucket");
    setShiftPopupVisible(true);
  };

  const saveShiftChange = async () => {
    const token = decryptData(sessionStorage.getItem('token'));
    try {
      const response = await fetch(`${API_BASE}/update-delivery-grouping`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", 'x-token': token },
        body: JSON.stringify({ 
          delivery_grouping_id: deliveryGroup._id, 
          is_delivered_by_brickbucket: shiftToValue === "Brickbucket",
          bill_to: billToValue 
        })
      });
      const res = await response.json();
      if (res.success) handleGetDg();
    } catch (error) {}
    setShiftPopupVisible(false);
  };

  // Fetch single bill dynamically when viewing per PO row
  const handleViewBill = async (subOrder) => {
    try {
      const poId = subOrder?.purchaseorder_id?._id || subOrder?.purchaseorder_id;
      const token = decryptData(sessionStorage.getItem('token'));
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}bill/get-bill-by-suborder-number?po_id=${poId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", 'x-token': token },
      });
      const data = await response.json();

      if (data.success) {
        setBillsData([data.data]);
        setBillPopup(true);
      } else {
        setPopupType('400'); setPopupMessage("Bill not found"); setShowPopup(true);
      }
    } catch (error) {
      setPopupType('500'); setPopupMessage("Something went wrong!"); setShowPopup(true);
    }
  };

  // Verify multiple bills logic
  const handleVerifyBills = async () => {
    try {
      const poIds = deliveryGroup.sub_orders
        .filter(so => so.billing_status === "Billed")
        .map(so => so.purchaseorder_id?._id || so.purchaseorder_id);
      
      if (!poIds.length) return;

      const token = decryptData(sessionStorage.getItem('token'));
      const promises = poIds.map(poId =>
        fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}bill/get-bill-by-suborder-number?po_id=${poId}`, {
          method: "GET",
          headers: { "Content-Type": "application/json", 'x-token': token },
        }).then(res => res.json())
      );

      const results = await Promise.all(promises);
      const bills = results.filter(r => r.success).map(r => r.data);
      
      if (bills.length > 0) {
        setBillsData(bills);
        setBillPopup(true);
      } else {
        setPopupType('400'); setPopupMessage("No bills found"); setShowPopup(true);
      }
    } catch (error) {
      setPopupType('500'); setPopupMessage("Something went wrong!"); setShowPopup(true);
    }
  };

  // Fetch single invoice per PO row
  const handleViewInvoice = async (subOrder) => {
    try {
      const poId = subOrder?.purchaseorder_id;
      if (!poId) return;
      const token = decryptData(sessionStorage.getItem('token'));
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}invoice/get-invoice-by-poid?po_id=${poId}`, {
        method: "GET", headers: { "Content-Type": "application/json", "x-token": token },
      });
      const data = await response.json();

      if (data.success && data.data) {
        setInvoicesData([data.data]);
        setInvoicesPopup(true);
      } else {
        setPopupType('400'); setPopupMessage(data.message || "Failed to fetch invoice"); setShowPopup(true);
      }
    } catch (error) {
      setPopupType('500'); setPopupMessage("Something went wrong!"); setShowPopup(true);
    }
  };

  // Fetch all invoices mapped to this Delivery Group
  const handleViewAllInvoices = async () => {
    try {
      const poIds = deliveryGroup.sub_orders
        .filter(so => so.invoice_number)
        .map(so => so.purchaseorder_id?._id || so.purchaseorder_id);
        
      if (!poIds.length) return;

      const token = decryptData(sessionStorage.getItem('token'));
      const promises = poIds.map(poId =>
        fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}invoice/get-invoice-by-poid?po_id=${poId}`, {
          method: "GET", headers: { "Content-Type": "application/json", "x-token": token },
        }).then(res => res.json())
      );

      const results = await Promise.all(promises);
      const invoices = results.filter(r => r.success && r.data).map(r => r.data);
      
      if (invoices.length > 0) {
        setInvoicesData(invoices);
        setInvoicesPopup(true);
      } else {
        setPopupType('400'); setPopupMessage("No invoices found"); setShowPopup(true);
      }
    } catch (error) {
      setPopupType('500'); setPopupMessage("Something went wrong!"); setShowPopup(true);
    }
  };

  const truncateFileName = (name, maxLen = 30) => {
    if (!name) return "";
    if (name.length <= maxLen) return name;
    const dotIdx = name.lastIndexOf(".");
    const ext = dotIdx !== -1 ? name.slice(dotIdx) : "";
    const base = dotIdx !== -1 ? name.slice(0, dotIdx) : name;
    const visible = Math.max(6, Math.floor((maxLen - ext.length - 3) / 2));
    return `${base.slice(0, visible)}...${base.slice(-visible)}${ext}`;
  };

  if (!deliveryGroup) return null;

  return (
    <div className="bg-white rounded-md shadow p-4 relative">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-full">
        
        {/* TOP HEADER */}
        <div className="px-5 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faBox} className="text-blue-600 text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Delivery Group</p>
                <p className="text-sm font-bold text-gray-900 truncate mt-0.5">{deliveryGroup.delivery_group_number || "—"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
                {deliveryGroup?.sub_orders?.length || 0} PO
              </span>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="grid grid-cols-1 lg:grid-cols-[310px_minmax(0,1fr)] flex-1">
          {/* LEFT - DELIVERY GROUP DETAILS */}
          <div className="border-b lg:border-b-0 lg:border-r border-gray-200 bg-gray-50/70">
            <div className="p-5">
              <div className="bg-white border border-gray-200 rounded-lg p-3.5 mb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Delivery Slot</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {deliveryGroup.delivery_date
                        ? new Date(deliveryGroup.delivery_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                        : "Not Scheduled"}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{deliveryGroup.delivery_slot || "No slot selected"}</p>
                  </div>
                  <button onClick={openSlotPopup} className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-blue-600 hover:bg-blue-50 transition flex-shrink-0">
                    <FontAwesomeIcon icon={faEdit} className="text-[9px]" /> Edit
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3.5 mb-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Bill To</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{deliveryGroup.bill_to || "Brickbucket"}</p>
                  </div>
                  <button
                    className={`px-2.5 py-1 text-[11px] font-medium border rounded-md transition ${!isBilled && !isBillingInitiated ? "text-blue-600 border-blue-200 hover:bg-blue-50" : "text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50"}`}
                    onClick={openEditPopup}
                    disabled={isBilled || isBillingInitiated}
                  >
                    Edit
                  </button>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-3.5 mb-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Ship To</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {deliveryGroup.is_delivered_by_brickbucket ? "Brickbucket" : "Customer"}
                    </p>
                  </div>
                  <button
                    className={`px-2.5 py-1 text-[11px] font-medium border rounded-md transition ${!isBilled && !isBillingInitiated ? "text-blue-600 border-blue-200 hover:bg-blue-50" : "text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50"}`}
                    onClick={openEditPopup}
                    disabled={isBilled || isBillingInitiated}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT - PURCHASE ORDERS */}
          <div className="min-w-0 flex flex-col">
            <div className="max-h-[500px] overflow-y-auto custom-scrollbar flex-1">
              {deliveryGroup?.sub_orders?.length > 0 ? (
                <div>
                  {deliveryGroup.sub_orders.map((subOrder, index) => {
                    const isExpanded = expandedPOs.includes(subOrder._id);
                    return (
                      <div key={subOrder._id} className={`border-b border-gray-300 last:border-b-0 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                        <div className={`px-5 py-3.5 transition ${isExpanded ? "bg-blue-50/40" : index % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50 hover:bg-gray-100"}`}>
                          <div className="flex items-center gap-3">
                            <div className="hidden sm:flex w-9 h-9 rounded-lg bg-white border border-gray-200 items-center justify-center flex-shrink-0 shadow-sm">
                              <FontAwesomeIcon icon={faBox} className="text-gray-400 text-xs" />
                            </div>
                            <div className="w-[150px] min-w-0">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">PO Number</p>
                              <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">{subOrder.po_number || subOrder.readable_sub_order_number || "—"}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Supplier</p>
                              <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">{subOrder.vendor_id?.company_name || subOrder.vendor_id?.name || "—"}</p>
                              <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">{subOrder.vendor_id?.mobile_number || "—"}</p>
                            </div>
                            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</span>
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">
                                {subOrder.status || "—"}
                              </span>
                            </div>
{/*                             <div className="flex-shrink-0">
                              {subOrder.billing_status === "Billed" ? (
                                <button onClick={() => handleViewBill(subOrder)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-green-200 bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100 transition whitespace-nowrap">
                                  <Eye size={13} />
                                  <span className="hidden sm:inline">View Bill</span>
                                </button>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </div>
                            <div className="flex-shrink-0">
                              {subOrder.invoice_number ? (
                                <button onClick={() => handleViewInvoice(subOrder)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-blue-200 bg-blue-50 text-blue-600 text-xs font-semibold hover:bg-blue-100 transition whitespace-nowrap">
                                  <Eye size={13} />
                                  <span className="hidden sm:inline">View Invoice</span>
                                </button>
                              ) : <span className="text-xs text-gray-400">—</span>}
                            </div> */}
                            <button onClick={() => setExpandedPOs(prev => prev.includes(subOrder._id) ? prev.filter(id => id !== subOrder._id) : [...prev, subOrder._id])} className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition flex-shrink-0">
                              <ChevronDown size={16} className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                          </div>
                        </div>

                        {/* EXPANDED PO */}
                        {isExpanded && (
                          <div className="px-5 pb-4 bg-blue-50/40 border-t border-blue-100">
                            <div className="pt-4 grid grid-cols-1 md:grid-cols-[120px_minmax(0,1fr)] gap-5">
                              <div>
                                {subOrder.po_images?.length > 0 ? (
                                  <img src={`${imageurl}${subOrder.po_images[0]}`} alt="PO" onClick={() => { setShowPreview(true); setSubOrderData(subOrder); }} className="w-28 h-28 object-cover rounded-lg cursor-pointer border border-gray-200 hover:opacity-80 transition shadow-sm" />
                                ) : (
                                  <div className="w-28 h-28 rounded-lg border border-dashed border-gray-300 bg-white flex items-center justify-center text-xs text-gray-400">Packed Items</div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                                  <div className="max-h-48 overflow-y-auto custom-scrollbar">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                          <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Item</th>
                                          <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-gray-500 uppercase">Quantity</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {subOrder.items?.length > 0 ? (
                                          subOrder.items.map((item) => (
                                            <tr key={item._id} className="hover:bg-gray-50">
                                              <td className="px-3 py-2.5 text-sm text-gray-900">{item.item_name}</td>
                                              <td className="px-3 py-2.5 text-right text-sm font-semibold text-gray-900">{item.quantity}</td>
                                            </tr>
                                          ))
                                        ) : <tr><td colSpan={2} className="px-3 py-5 text-center text-xs text-gray-400">No items found</td></tr>}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="min-h-[300px] flex flex-col items-center justify-center text-center p-10">
                  <div className="w-12 h-12 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center mb-3">
                    <FontAwesomeIcon icon={faBoxesStacked} className="text-gray-300 text-lg" />
                  </div>
                  <p className="text-sm font-semibold text-gray-500">No Purchase Orders</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* ACTION BUTTON FOOTER */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          
          {isBillingInitiated && (
            <span className="px-5 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
              Billing process initiated
            </span>
          )}

          {!isBilled && !isBillingInitiated && (
            <button
              onClick={handleGenerateBill}
              disabled={isInitiateBillingLoading}
              className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg shadow-md transition ${isInitiateBillingLoading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
            >
              {isInitiateBillingLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                  Initiating…
                </span>
              ) : "Initiate Billing"}
            </button>
          )}

          {isBilled && (
            <button
              onClick={handleVerifyBills}
              className="px-5 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 transition"
            >
              {isBillAccepted ? "View Bills" : "Verify Bills"}
            </button>
          )}

          {isBillAccepted && !isInvoiceGenerated && !isReadyForDelivery && (
            <button
              onClick={handleGenerateInvoices}
              disabled={isGeneratingInvoice}
              className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg shadow-md transition flex items-center justify-center gap-2 ${isGeneratingInvoice ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
            >
              {isGeneratingInvoice ? (
                <><svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg> Generating…</>
              ) : "Generate Invoice"}
            </button>
          )}

          {isInvoiceGenerated && (
             <button
               onClick={handleViewAllInvoices}
               className="px-5 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 transition"
             >
               View Invoices
             </button>
          )}

          {isReadyForDelivery && (
            <button
              className="px-5 py-2.5 text-sm font-medium text-white rounded-lg shadow-md transition bg-red-600 hover:bg-red-700"
              onClick={() => navigate('/bb/app/home', { state: { activeTab: 'deliverys' } })}>
              Initiate Delivery Process
            </button>
          )}
        </div>
      </div>

      {/* POPUPS (Modals) */}
      {showSlotPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-100 shadow-xl flex flex-col">
            <h3 className="text-lg font-semibold mb-4">Change Delivery Slot</h3>
            <div className="mb-4">
              <label className="text-sm font-medium">Select Date</label>
              <input type="date" value={slotDate} onChange={(e) => setSlotDate(e.target.value)} className="w-full border px-3 py-2 rounded-md mt-1" />
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium">Delivery Slot</label>
              <select value={slotTime} onChange={(e) => setSlotTime(e.target.value)} className="w-full border px-3 py-2 rounded-md mt-1">
                <option value="">Select Slot</option>
                <option value="2:00 PM – 7:00 PM">2:00 PM – 7:00 PM</option>
                <option value="9:30 AM – 1:00 PM">9:30 AM – 1:00 PM</option>
              </select>
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <button onClick={() => setShowSlotPopup(false)} className="px-3 py-1 bg-gray-300 rounded-md">Cancel</button>
              <button onClick={saveSlotUpdate} className="px-3 py-1 bg-red-500 text-white rounded-md">Save</button>
            </div>
          </div>
        </div>
      )}

      {shiftPopupVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white w-[90%] max-w-sm rounded-lg p-6 shadow-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-6">Update Details</h3>
            <div className="mb-6">
              <p className="font-semibold text-gray-700 mb-2">Bill To</p>
              <select value={billToValue} onChange={(e) => setBillToValue(e.target.value)} className="w-full border rounded p-2 bg-white">
                <option value="Brickbucket">Brickbucket</option>
                <option value="Customer">Customer</option>
              </select>
            </div>
            <div className="mb-6">
              <p className="font-semibold text-gray-700 mb-2">Ship To</p>
              <select value={shiftToValue} onChange={(e) => setShiftToValue(e.target.value)} className="w-full border rounded p-2 bg-white">
                <option value="Brickbucket">Brickbucket</option>
                <option value="Customer">Customer</option>
              </select>
            </div>
            <div className="flex justify-end gap-6 border-t border-gray-200 pt-4">
              <button className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400" onClick={() => setShiftPopupVisible(false)}>Cancel</button>
              <button className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={saveShiftChange}>Save</button>
            </div>
          </div>
        </div>
      )}

      {billPopup && billsData?.length > 0 && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-start z-50 p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-xl rounded-xl p-6 shadow-2xl my-8">
            <div className="flex justify-between items-center mb-5 sticky top-0 bg-white z-10 pb-2 border-b">
              <h2 className="text-xl font-semibold text-gray-800">Bill Details</h2>
              <button onClick={() => setBillPopup(false)}>
                <X size={24} className="text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            
            <div className="space-y-6">
              {billsData.map((bill) => {
                const state = billStates[bill._id] || {};
                return (
                  <div key={bill._id} className="border border-gray-200 rounded-lg p-5 bg-white shadow-sm">
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-700 block mb-2">Bill Number</label>
                      <div className="flex items-center gap-3">
                        <input 
                          type="text" 
                          value={state.billNumber || ""} 
                          disabled={!state.editBill} 
                          onChange={(e) => updateBillState(bill._id, 'billNumber', e.target.value)} 
                          className={`flex-1 px-3 py-2 border rounded-lg ${state.editBill ? "bg-white border-blue-500" : "bg-gray-100 cursor-not-allowed"}`} 
                        />
                        {!state.editBill && (
                          <button onClick={() => updateBillState(bill._id, 'editBill', true)} className="text-blue-600 p-2">
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <p className="font-medium mb-3 text-gray-800">Files</p>
                      <div className="space-y-3">
                        {bill.files?.map((file, idx) => (
                          <div key={idx} className="border p-3 rounded-lg flex items-center justify-between bg-gray-50">
                            <span className="text-sm font-medium">{truncateFileName(file.split("/").pop(), 34)}</span>
                            <a href={imageurl + file} target="_blank" rel="noreferrer" className="text-blue-600 text-sm underline">View</a>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {!state.editBill && !bill.bill_accepted && bill.reject_reason && (
                      <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-xl mt-4">
                        <h4 className="font-semibold text-orange-800 mb-1">Bill Rejected</h4>
                        <p className="text-sm">{bill.reject_reason}</p>
                      </div>
                    )}
                    
                    {((state.showRejectReason && !bill.reject_reason?.trim()) || state.editBill) && !bill.bill_accepted && (
                      <div className="mb-6 p-2 mt-2 bg-gray-50 border rounded-lg">
                        <label className="block text-sm font-semibold text-red-800 mb-2">Rejection Reason *</label>
                        <textarea 
                          value={state.rejectReason || ""} 
                          onChange={(e) => updateBillState(bill._id, 'rejectReason', e.target.value)} 
                          className="w-full border rounded-lg p-3" 
                          rows={3} 
                        />
                      </div>
                    )}
                    
                    <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-gray-100">
                      {!state.editBill && !bill.bill_accepted && (
                        <div className="flex gap-4">
                          {bill?.reject_reason || !state.showRejectReason ? (
                            <>
                              <button 
                                onClick={() => { 
                                  updateBillState(bill._id, 'rejectReason', ""); 
                                  updateBillState(bill._id, 'showRejectReason', true); 
                                }} 
                                className="px-6 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg flex items-center gap-2"
                              >
                                <FontAwesomeIcon icon={faTimes} /> Reject
                              </button>
                              <button 
                                onClick={() => handleAcceptBill(bill._id)} 
                                className="px-6 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg flex items-center gap-2"
                              >
                                <FontAwesomeIcon icon={faCheck} /> Accept
                              </button>
                            </>
                          ) : (
                            <button 
                              onClick={() => handleRejectBill(bill._id, state.rejectReason)} 
                              disabled={!state.rejectReason?.trim()} 
                              className={`px-4 py-2 rounded-lg ${!state.rejectReason?.trim() ? "bg-gray-100 text-gray-400" : "bg-red-600 text-white"}`}
                            >
                              Save Rejection
                            </button>
                          )}
                        </div>
                      )}
                      {state.editBill && (
                        <>
                          <button onClick={() => updateBillState(bill._id, 'editBill', false)} className="px-4 py-2 bg-gray-200 rounded-lg">Cancel</button>
                          <button onClick={() => updateBillNumber(bill._id, state.billNumber, state.rejectReason)} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Update</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {invoicesPopup && invoicesData?.length > 0 && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-start z-50 p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-lg rounded-xl p-6 shadow-2xl my-8">
            <div className="flex justify-between items-center mb-5">
               <h2 className="text-xl font-semibold text-gray-800">Invoices</h2>
               <button onClick={() => setInvoicesPopup(false)}>
                 <X size={24} className="text-gray-500 hover:text-gray-700" />
               </button>
            </div>
            <div className="space-y-4">
               {invoicesData.map((inv, i) => {
                   const pdfFile = inv.files?.find(file => file.toLowerCase().endsWith(".pdf"));
                   return (
                       <div key={i} className="border p-4 rounded-lg bg-gray-50 flex justify-between items-center">
                           <div>
                               <p className="font-semibold text-sm">Invoice No: {inv.invoice_number || "N/A"}</p>
                           </div>
                           {pdfFile ? (
                               <a href={imageurl + pdfFile} target="_blank" rel="noreferrer" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition">
                                 View PDF
                               </a>
                           ) : (
                               <span className="text-xs text-gray-500">No PDF File</span>
                           )}
                       </div>
                   )
               })}
            </div>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl p-1 w-full max-w-4xl relative shadow-xl">
            <button onClick={() => setShowPreview(false)} className="absolute top-2 right-3 text-gray-700 text-xl font-bold">✕</button>
            <div className="flex overflow-x-auto gap-6 py-4">
              {subOrderData?.po_images?.map((img, idx) => (
                <div key={idx} className="min-w-[300px] flex justify-center">
                  <img src={imageurl + img} className="max-h-[450px] rounded-lg object-contain border shadow-md" alt="PO" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenderDeliveryGroupMapping;